import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, CircularProgress, CssBaseline } from '@mui/material';
import Layout from './components/Layout';
import { useAuth } from './hooks/useAuth';
import { Permission, UserRole } from './types';

const Login = React.lazy(() => import('./pages/Login'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const OrderManagement = React.lazy(() => import('./pages/OrderManagement'));
const CollectionOrders = React.lazy(() => import('./pages/CollectionOrders'));
const PayoutOrders = React.lazy(() => import('./pages/PayoutOrders'));
const Merchants = React.lazy(() => import('./pages/Merchants'));
const PaymentAccountsV2 = React.lazy(() => import('./pages/PaymentAccountsV2Simple'));
const Users = React.lazy(() => import('./pages/Users'));
const AgentManagement = React.lazy(() => import('./pages/AgentManagement'));
const MerchantKeyManagement = React.lazy(() => import('./pages/MerchantKeyManagement'));
const MerchantProfile = React.lazy(() => import('./pages/MerchantProfile'));
const PaymentTest = React.lazy(() => import('./pages/PaymentTest'));
const Orders = React.lazy(() => import('./pages/Orders'));
const LimitManagement = React.lazy(() => import('./pages/LimitManagement'));
const AcceptanceRequest = React.lazy(() => import('./pages/merchant/AcceptanceRequest'));
const ComplaintRecords = React.lazy(() => import('./pages/merchant/ComplaintRecords'));
const AcceptanceManagement = React.lazy(() => import('./pages/admin/AcceptanceManagement'));
const ComplaintDeductions = React.lazy(() => import('./pages/admin/ComplaintDeductions'));
const CustomerManagement = React.lazy(() => import('./pages/CustomerManagement'));
const ApiDocs = React.lazy(() => import('./pages/ApiDocs'));
const MerchantStatistics = React.lazy(() => import('./pages/MerchantStatistics'));
const UTRApplications = React.lazy(() => import('./pages/UTRApplications'));
const UTRApplication = React.lazy(() => import('./pages/merchant/UTRApplication'));
const BalanceAdjustment = React.lazy(() => import('./pages/BalanceAdjustment'));
const SettlementManagement = React.lazy(() => import('./pages/SettlementManagement'));
const ChannelCollection = React.lazy(() => import('./pages/ChannelCollection'));
const PaymentUrlChannels = React.lazy(() => import('./pages/PaymentUrlChannels'));

const appFontFamily = [
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  '"PingFang SC"',
  '"Heiti SC"',
  '"Microsoft YaHei"',
  'Roboto',
  'Helvetica',
  'Arial',
  'sans-serif'
].join(', ');

// 创建主题
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: appFontFamily,
    allVariants: {
      fontFamily: appFontFamily,
    },
    button: {
      fontFamily: appFontFamily,
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontFamily: appFontFamily,
        },
        body: {
          fontFamily: appFontFamily,
        },
        '#root': {
          fontFamily: appFontFamily,
        },
        'button, input, textarea, select': {
          fontFamily: 'inherit',
        },
        '.ant-typography, .ant-btn, .ant-input, .ant-select, .ant-table': {
          fontFamily: `${appFontFamily} !important`,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: appFontFamily,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: appFontFamily,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: appFontFamily,
          fontSize: '0.875rem',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: appFontFamily,
          fontSize: '0.875rem',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: appFontFamily,
          fontSize: '0.875rem',
        },
        head: {
          fontWeight: 700,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: appFontFamily,
        },
        label: {
          fontFamily: appFontFamily,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: appFontFamily,
          fontSize: '0.875rem',
        },
      },
    },
  },
});

interface AppPageRoute {
  path: string;
  component: React.ComponentType;
  permissions?: Permission[];
  anyPermission?: Permission[];
  requiredRole?: UserRole;
}

const PageLoader: React.FC = () => (
  <Box
    sx={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    <CircularProgress size={28} />
  </Box>
);

const appPageRoutes: AppPageRoute[] = [
  { path: '/', component: Dashboard },
  {
    path: '/orders',
    component: Orders,
    anyPermission: [Permission.VIEW_ALL_TRANSACTIONS, Permission.VIEW_OWN_ORDERS]
  },
  {
    path: '/transactions',
    component: OrderManagement,
    anyPermission: [Permission.VIEW_ALL_TRANSACTIONS, Permission.VIEW_OWN_TRANSACTIONS]
  },
  {
    path: '/collection-orders',
    component: CollectionOrders,
    anyPermission: [Permission.VIEW_ALL_TRANSACTIONS, Permission.VIEW_OWN_TRANSACTIONS]
  },
  {
    path: '/payout-orders',
    component: PayoutOrders,
    anyPermission: [Permission.VIEW_ALL_TRANSACTIONS, Permission.VIEW_OWN_TRANSACTIONS]
  },
  {
    path: '/merchants',
    component: Merchants,
    anyPermission: [Permission.VIEW_ALL_MERCHANTS, Permission.VIEW_AGENT_MERCHANTS]
  },
  {
    path: '/payment-accounts',
    component: PaymentAccountsV2,
    permissions: [Permission.VIEW_PAYMENT_CONFIG]
  },
  {
    path: '/channel-collection',
    component: ChannelCollection,
    anyPermission: [Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
  },
  {
    path: '/payment-url-channels',
    component: PaymentUrlChannels,
    requiredRole: UserRole.ADMIN
  },
  {
    path: '/users',
    component: Users,
    permissions: [Permission.MANAGE_USERS]
  },
  {
    path: '/agent-management',
    component: AgentManagement,
    permissions: [Permission.MANAGE_USERS]
  },
  { path: '/limit-management', component: LimitManagement },
  {
    path: '/payment-test',
    component: PaymentTest,
    requiredRole: UserRole.ADMIN
  },
  {
    path: '/profile',
    component: MerchantProfile,
    requiredRole: UserRole.MERCHANT
  },
  {
    path: '/merchant-keys',
    component: MerchantKeyManagement,
    requiredRole: UserRole.MERCHANT
  },
  {
    path: '/acceptance-request',
    component: AcceptanceRequest,
    requiredRole: UserRole.MERCHANT
  },
  {
    path: '/complaint-records',
    component: ComplaintRecords,
    requiredRole: UserRole.MERCHANT
  },
  {
    path: '/acceptance-management',
    component: AcceptanceManagement,
    requiredRole: UserRole.ADMIN
  },
  {
    path: '/balance-adjustment',
    component: BalanceAdjustment,
    anyPermission: [Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
  },
  {
    path: '/complaint-deductions',
    component: ComplaintDeductions,
    anyPermission: [Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
  },
  {
    path: '/settlement-management',
    component: SettlementManagement,
    requiredRole: UserRole.ADMIN
  },
  {
    path: '/customer-management',
    component: CustomerManagement,
    anyPermission: [Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
  },
  {
    path: '/api-docs',
    component: ApiDocs,
    requiredRole: UserRole.MERCHANT
  },
  {
    path: '/merchant-statistics',
    component: MerchantStatistics,
    anyPermission: [Permission.VIEW_AGENT_STATISTICS, Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
  },
  {
    path: '/utr-applications',
    component: UTRApplications,
    anyPermission: [Permission.MANAGE_USERS, Permission.SYSTEM_MONITORING]
  },
  {
    path: '/utr-application',
    component: UTRApplication,
    requiredRole: UserRole.MERCHANT
  }
];

const AdminShell: React.FC = () => {
  const { isAuthenticated, currentUser, hasPermission } = useAuth();
  const location = useLocation();
  const currentRoute = appPageRoutes.find(route => route.path === location.pathname);

  if (!isAuthenticated || !currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!currentRoute) {
    return <Navigate to="/" replace />;
  }

  if (currentRoute.requiredRole && currentUser.role !== currentRoute.requiredRole) {
    return <Navigate to="/" replace />;
  }

  if (currentRoute.permissions && currentRoute.permissions.length > 0) {
    const hasAllPermissions = currentRoute.permissions.every(hasPermission);
    if (!hasAllPermissions) return <Navigate to="/" replace />;
  }

  if (currentRoute.anyPermission && currentRoute.anyPermission.length > 0) {
    const hasAnyPermission = currentRoute.anyPermission.some(hasPermission);
    if (!hasAnyPermission) return <Navigate to="/" replace />;
  }

  return (
    <Layout
      renderPage={(path) => {
        const route = appPageRoutes.find(item => item.path === path);
        if (!route) return null;
        const PageComponent = route.component;
        return <PageComponent />;
      }}
    />
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/login"
              element={<Login />}
            />
            <Route path="/*" element={<AdminShell />} />
          </Routes>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
}

export default App;
